/**
 * Hidden-directive parsing + html.patch helpers for the chat hook (useChat).
 *
 * These symbols are re-exported from useChat.ts so importer paths stay stable —
 * tests import sanitizeHiddenDirectives, endsWithQuestion, handleHtmlPatchDirective,
 * and hasExplicitRebuildIntent from ./useChat.
 */

import type { ChatUIAction } from '@/lib/api/endpoints/chat'
import { applyDirectionSanityToHtml } from '../hooks/direction-sanity'
import type { QuickAction } from '../hooks/useChat.types'

const UI_DIRECTIVE_TOKEN = '{{XAVIOR_UI'
const STATUS_DIRECTIVE_TOKEN = '{{XAVIOR_STATUS'
const ACTIONS_DIRECTIVE_TOKEN = '{{XAVIOR_ACTIONS'
// Legacy: the old StructuralEditTool path before its uiActions migration
// emitted `{{XAVIOR_DIRECTIVE:edit_section:...}}` inside assistant text.
// After the migration nothing emits this, but persisted history (and
// LLM-echoed mirrors of historical context) can still contain the token.
// extractHiddenDirectives recognises and drops it silently on replay.
const LEGACY_STRUCTURAL_DIRECTIVE_TOKEN = '{{XAVIOR_DIRECTIVE:'

/**
 * Onboarding chip-fallback predicate (#1718). True when the assistant's
 * final visible turn ends with a question mark — possibly followed by
 * closing punctuation/whitespace (quotes, brackets, trailing markdown).
 * Used by the onboarding chip-render fallback so users are never stranded
 * waiting for a missing `{{XAVIOR_ACTIONS}}` directive.
 */
export function endsWithQuestion(text: string): boolean {
  if (!text) return false
  // Strip trailing whitespace + closing punctuation that may follow a `?`
  // (e.g. `?"`, `?)`, `?**`). Stops at the first `?` we find.
  const trimmed = text.replace(/[\s"'’”)\]*_`]+$/u, '').trimEnd()
  return trimmed.endsWith('?')
}

function findDirectiveJsonBounds(source: string): { start: number; end: number } | null {
  let idx = 0
  while (idx < source.length && /\s/.test(source[idx] ?? '')) idx += 1
  const start = idx
  const startChar = source[start] ?? ''
  if (startChar !== '{' && startChar !== '[') return null

  let depth = 0
  let inString = false
  let escaped = false

  for (let i = start; i < source.length; i += 1) {
    const char = source[i] ?? ''
    if (inString) {
      if (escaped) {
        escaped = false
        continue
      }
      if (char === '\\') {
        escaped = true
        continue
      }
      if (char === '"') inString = false
      continue
    }

    if (char === '"') {
      inString = true
      continue
    }
    if (char === '{' || char === '[') {
      depth += 1
      continue
    }
    if (char === '}' || char === ']') {
      depth -= 1
      if (depth === 0) return { start, end: i + 1 }
    }
  }
  return null
}

function normalizeDirectiveUiActions(input: unknown): ChatUIAction[] {
  const list = Array.isArray(input)
    ? input
    : (input && typeof input === 'object' && Array.isArray((input as { actions?: unknown[] }).actions)
      ? (input as { actions: unknown[] }).actions
      : [input])

  const actions: ChatUIAction[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const action = item as ChatUIAction
    if (typeof action.type !== 'string' || !action.type.trim()) continue
    actions.push(action)
  }
  return actions
}

function normalizeDirectiveStatusMessages(input: unknown): string[] {
  const messages: string[] = []
  const pushMessage = (value: unknown) => {
    if (typeof value !== 'string') return
    const trimmed = value.trim()
    if (!trimmed) return
    messages.push(trimmed.slice(0, 180))
  }

  if (typeof input === 'string') {
    pushMessage(input)
    return messages
  }
  if (Array.isArray(input)) {
    for (const value of input) pushMessage(value)
    return messages
  }
  if (input && typeof input === 'object') {
    const payload = input as Record<string, unknown>
    pushMessage(payload.message)
    pushMessage(payload.status)
    if (Array.isArray(payload.messages)) {
      for (const value of payload.messages) pushMessage(value)
    }
  }
  return messages
}

// ─── Exported helpers for html.patch + rebuild-guard (testable in isolation) ──

/**
 * Returns true when the user's last message contains explicit rebuild intent
 * ("start over", "rebuild", "redesign from scratch", etc.). Used by the
 * html.init guard to distinguish "user asked for a small tweak" (reject
 * rebuild) from "user asked for a full redo" (allow rebuild).
 *
 * Exported so the regex is pinnable by tests and stays centralised — any
 * later tuning happens in one place.
 */
export function hasExplicitRebuildIntent(userMessage: string): boolean {
  // Broad coverage of phrasings users actually type when they want a full
  // rebuild. Kept explicit rather than fuzzy so the decision is predictable.
  return /\b(start over|from scratch|rebuild|redesign|remake|redo|do over|make a new version|another version|version 2|v2 please|start again|completely new|brand new|scrap it|new design|throw (?:it|this) out|nuke it|delete and (?:rebuild|redo|start))\b/i.test(
    userMessage,
  );
}



/**
 * Depth-tracking section finder that handles nested same-type elements.
 * Returns the byte range [start, end) of the element whose data-section-id
 * matches `sid`, or null when the section is absent from `html`.
 *
 * Exported so unit tests can pin the finding logic without mounting useChat.
 */
export function findSectionRange(html: string, sid: string): { start: number; end: number } | null {
  const openPattern = new RegExp(
    `<(\\w+)([^>]*\\bdata-section-id\\s*=\\s*["']${sid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["'][^>]*)>`,
    'i'
  )
  const openMatch = openPattern.exec(html)
  if (!openMatch) return null
  const tagName = openMatch[1]
  const startIdx = openMatch.index
  let depth = 1
  let i = startIdx + openMatch[0].length
  const openTag = new RegExp(`<${tagName}[\\s>/]`, 'gi')
  const closeTag = new RegExp(`</${tagName}\\s*>`, 'gi')
  while (depth > 0 && i < html.length) {
    openTag.lastIndex = i
    closeTag.lastIndex = i
    const nextOpen = openTag.exec(html)
    const nextClose = closeTag.exec(html)
    if (!nextClose) break
    if (nextOpen && nextOpen.index < nextClose.index) {
      depth++
      i = nextOpen.index + nextOpen[0].length
    } else {
      depth--
      if (depth === 0) {
        return { start: startIdx, end: nextClose.index + nextClose[0].length }
      }
      i = nextClose.index + nextClose[0].length
    }
  }
  return null
}

/**
 * Apply an html.patch directive and dispatch the appropriate CustomEvents.
 *
 * When the target section is absent from `currentHtml`, fires a
 * `xavior:patch-failed` CustomEvent and logs a console warning instead of
 * silently swallowing the edit.
 *
 * Returns `{ patchedHtml: string, sectionDeltaHtml: string, sectionDeltaId: string }`
 * on success, or `null` when the section was not found.
 *
 * Exported so unit tests can call it directly without mounting the hook.
 */
export function handleHtmlPatchDirective(input: {
  moduleId: string
  sectionId: string
  html: string
  currentHtml: string
  userIntent?: string
}): { patchedHtml: string; sectionDeltaHtml: string; sectionDeltaId: string } | null {
  const { moduleId, sectionId, html: newHtml, currentHtml, userIntent } = input
  const range = findSectionRange(currentHtml, sectionId)
  if (!range) {
    console.warn('[useChat] html.patch target section not found:', sectionId)
    window.dispatchEvent(new CustomEvent('xavior:patch-failed', {
      detail: { moduleId, sectionId, reason: 'section-not-found' },
    }))
    return null
  }
  const oldSection = currentHtml.slice(range.start, range.end)
  let effectiveNewHtml = newHtml
  if (userIntent && userIntent.trim()) {
    const result = applyDirectionSanityToHtml(oldSection, newHtml, userIntent)
    if (result.corrections.length > 0) {
      effectiveNewHtml = result.corrected
      window.dispatchEvent(new CustomEvent('xavior:direction-corrected', {
        detail: { moduleId, sectionId, corrections: result.corrections, userIntent },
      }))
      for (const c of result.corrections) {
        console.info(`[useChat] direction-sanity flipped ${c.property}: ${c.before} → ${c.after}`)
      }
    }
  }
  const patchedHtml = currentHtml.slice(0, range.start) + effectiveNewHtml + currentHtml.slice(range.end)
  return { patchedHtml, sectionDeltaHtml: effectiveNewHtml, sectionDeltaId: sectionId }
}

export function normalizeQuickActions(input: unknown): QuickAction[] {
  const list = Array.isArray(input) ? input : []
  const actions: QuickAction[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const qa = item as Record<string, unknown>
    if (typeof qa.label !== 'string' || typeof qa.message !== 'string') continue
    actions.push({
      label: qa.label,
      message: qa.message,
      icon: typeof qa.icon === 'string' ? qa.icon : undefined,
      variant: qa.variant === 'primary' ? 'primary' : qa.variant === 'outline' ? 'outline' : undefined,
      kind: typeof qa.kind === 'string' ? qa.kind : undefined,
    })
  }
  return actions
}

export function extractActionsDirective(content: string): {
  cleanContent: string
  quickActions: QuickAction[]
} {
  const quickActions: QuickAction[] = []
  let result = content

  while (true) {
    const idx = result.indexOf(ACTIONS_DIRECTIVE_TOKEN)
    if (idx === -1) break

    const afterToken = result.slice(idx + ACTIONS_DIRECTIVE_TOKEN.length)
    const delimiterMatch = afterToken.match(/^\s*:?\s*/)
    const delimiterLength = delimiterMatch?.[0].length ?? 0
    const afterPrefix = afterToken.slice(delimiterLength)
    const jsonBounds = findDirectiveJsonBounds(afterPrefix)

    if (!jsonBounds) {
      const firstNonWs = afterPrefix.trimStart()[0]
      if (firstNonWs === '{' || firstNonWs === '[') break
      const closeIdx = afterPrefix.indexOf('}}')
      if (closeIdx === -1) break
      result = result.slice(0, idx) + afterPrefix.slice(closeIdx + 2)
      continue
    }

    const afterJson = afterPrefix.slice(jsonBounds.end)
    const wsMatch = afterJson.match(/^\s*/)
    const wsLen = wsMatch?.[0].length || 0
    const afterWs = afterJson.slice(wsLen)
    if (!afterWs.startsWith('}}')) break

    const payloadRaw = afterPrefix.slice(jsonBounds.start, jsonBounds.end)
    try {
      const parsed = JSON.parse(payloadRaw)
      quickActions.push(...normalizeQuickActions(parsed))
    } catch {
      // swallow
    }

    const consumed = idx + ACTIONS_DIRECTIVE_TOKEN.length + delimiterLength + jsonBounds.end + wsLen + 2
    result = result.slice(0, idx) + result.slice(consumed)
  }

  return { cleanContent: result.trim(), quickActions }
}

function getDirectiveJoiner(
  isUiDirective: boolean,
  previousChar: string,
  nextChar: string,
): string {
  if (!previousChar || !nextChar) return ''
  if (/\s/.test(previousChar) || /\s/.test(nextChar)) return ''

  // Status directives often split "phase 1" and "phase 2" assistant text.
  // Preserve a clear boundary so chunks don't read as pasted together.
  if (!isUiDirective) return '\n\n'

  // UI directives should be silent; only repair obvious sentence boundaries.
  if (/[.!?]/.test(previousChar) && /[A-Za-z]/.test(nextChar)) return ' '
  return ''
}

export function extractHiddenDirectives(input: string): {
  visibleText: string
  remainder: string
  uiActions: ChatUIAction[]
  statusMessages: string[]
  quickActions: QuickAction[]
} {
  let working = input
  let visibleText = ''
  const uiActions: ChatUIAction[] = []
  const statusMessages: string[] = []
  const quickActions: QuickAction[] = []

  while (true) {
    const uiIdx = working.indexOf(UI_DIRECTIVE_TOKEN)
    const statusIdx = working.indexOf(STATUS_DIRECTIVE_TOKEN)
    const actionsIdx = working.indexOf(ACTIONS_DIRECTIVE_TOKEN)
    const legacyIdx = working.indexOf(LEGACY_STRUCTURAL_DIRECTIVE_TOKEN)
    if (uiIdx === -1 && statusIdx === -1 && actionsIdx === -1 && legacyIdx === -1) {
      visibleText += working
      return { visibleText, remainder: '', uiActions, statusMessages, quickActions }
    }

    // Find the earliest directive
    const candidates = [
      uiIdx !== -1 ? uiIdx : Infinity,
      statusIdx !== -1 ? statusIdx : Infinity,
      actionsIdx !== -1 ? actionsIdx : Infinity,
      legacyIdx !== -1 ? legacyIdx : Infinity,
    ]
    const minIdx = Math.min(...candidates)
    const isLegacyDirective = minIdx === legacyIdx && legacyIdx !== -1

    if (isLegacyDirective) {
      // Legacy {{XAVIOR_DIRECTIVE:...}} — strip without dispatch. Real
      // emissions go through metadata.uiActions; this only catches
      // persisted/echoed historical tokens. Logged so a live regression
      // (anything emitting the legacy token at runtime) is visible in
      // the console instead of vanishing silently.
      visibleText += working.slice(0, minIdx)
      const afterToken = working.slice(minIdx + LEGACY_STRUCTURAL_DIRECTIVE_TOKEN.length)
      const closeIdx = afterToken.indexOf('}}')
      if (closeIdx === -1) {
        // Incomplete (streaming chunk boundary) — hold in remainder.
        return { visibleText, remainder: working.slice(minIdx), uiActions, statusMessages, quickActions }
      }
      console.warn(
        '[useChat] stripped legacy XAVIOR_DIRECTIVE token (expected metadata.uiActions path). Sample:',
        afterToken.slice(0, Math.min(closeIdx, 80)),
      )
      working = afterToken.slice(closeIdx + 2)
      continue
    }

    const isUiDirective = minIdx === uiIdx && uiIdx !== -1
    const isActionsDirective = !isUiDirective && minIdx === actionsIdx && actionsIdx !== -1
    const directiveToken = isUiDirective
      ? UI_DIRECTIVE_TOKEN
      : isActionsDirective
        ? ACTIONS_DIRECTIVE_TOKEN
        : STATUS_DIRECTIVE_TOKEN
    const prefixIdx = minIdx

    visibleText += working.slice(0, prefixIdx)
    const afterDirectiveToken = working.slice(prefixIdx + directiveToken.length)
    const delimiterMatch = afterDirectiveToken.match(/^\s*:?\s*/)
    const delimiterLength = delimiterMatch?.[0].length ?? 0
    const afterPrefix = afterDirectiveToken.slice(delimiterLength)
    const jsonBounds = findDirectiveJsonBounds(afterPrefix)

    if (!jsonBounds) {
      const firstNonWs = afterPrefix.trimStart()[0]
      if (firstNonWs === '{' || firstNonWs === '[') {
        // JSON payload started but isn't complete yet (streaming chunk boundary).
        // Hold in remainder so subsequent chunks can complete it.
        return { visibleText, remainder: working.slice(prefixIdx), uiActions, statusMessages, quickActions }
      }
      const closeIdx = afterPrefix.indexOf('}}')
      if (closeIdx === -1) {
        return { visibleText, remainder: working.slice(prefixIdx), uiActions, statusMessages, quickActions }
      }
      working = afterPrefix.slice(closeIdx + 2)
      continue
    }

    const afterJson = afterPrefix.slice(jsonBounds.end)
    const whitespaceMatch = afterJson.match(/^\s*/)
    const whitespaceLength = whitespaceMatch?.[0].length || 0
    const afterWhitespace = afterJson.slice(whitespaceLength)
    if (!afterWhitespace.startsWith('}}')) {
      return { visibleText, remainder: working.slice(prefixIdx), uiActions, statusMessages, quickActions }
    }

    const payloadRaw = afterPrefix.slice(jsonBounds.start, jsonBounds.end)
    try {
      const parsed = JSON.parse(payloadRaw)
      if (isUiDirective) {
        uiActions.push(...normalizeDirectiveUiActions(parsed))
      } else if (isActionsDirective) {
        quickActions.push(...normalizeQuickActions(parsed))
      } else {
        statusMessages.push(...normalizeDirectiveStatusMessages(parsed))
      }
    } catch {
      // swallow malformed hidden directive payloads
    }

    const consumedAfterPrefix = jsonBounds.end + whitespaceLength + 2
    const nextWorking = afterPrefix.slice(consumedAfterPrefix)
    const joiner = getDirectiveJoiner(
      isUiDirective || isActionsDirective,
      visibleText[visibleText.length - 1] ?? '',
      nextWorking[0] ?? '',
    )
    if (joiner) visibleText += joiner
    working = nextWorking
  }
}

export function sanitizeHiddenDirectives(input: string): {
  content: string
  uiActions: ChatUIAction[]
  statusMessages: string[]
} {
  const extracted = extractHiddenDirectives(input)
  return {
    content: extracted.visibleText,
    uiActions: extracted.uiActions,
    statusMessages: extracted.statusMessages,
  }
}
